import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { type OptionPickerDefaultValue } from '@blaze-shared/elements-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class DropdownPropsBuilder extends PropsBuilder<'dropdown'> {
  override setPlaceholder = (placeholder: string | undefined): this => {
    this.patchProps({ content: { menu: { placeholder } } });
    return this;
  };

  override setDefaultValue = (defaultValue: OptionPickerDefaultValue | undefined): this => {
    this.patchProps({ content: { data: { defaultValue } } });
    return this;
  };

  override setMargin = (margin: string | undefined): this => {
    this.patchProps({ style: { general: { offset: { margin } } } });
    return this;
  };

  override setParentContentProps = (
    elementProps: PartialPropsOf<'dropdown'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'dropdown'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'dropdown'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'dropdown'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: { title: undefined, accent: undefined },
          input: undefined,
          list: undefined,
        },
      });
      return this;
    }

    const { titleLabel, fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (shouldPatchProp(fieldLabel?.color, 'style.input.color', elementProps, initialValues)) {
      this.patchProps({ style: { input: { color: fieldLabel.color } } });
    }
    if (shouldPatchProp(fieldLabel?.color, 'style.list.value', elementProps, initialValues)) {
      this.patchProps({ style: { list: { value: fieldLabel.color } } });
    }

    if (shouldPatchProp(fieldBox?.accent, 'style.general.accent', elementProps, initialValues)) {
      this.patchProps({ style: { general: { accent: fieldBox.accent } } });
    }
    if (shouldPatchProp(fieldBox?.fill, 'style.input.fill', elementProps, initialValues)) {
      this.patchProps({ style: { input: { fill: fieldBox.fill } } });
    }
    if (shouldPatchProp(fieldBox?.fill, 'style.list.background', elementProps, initialValues)) {
      this.patchProps({ style: { list: { background: fieldBox.fill } } });
    }
    if (shouldPatchProp(fieldBox?.outline, 'style.input.border', elementProps, initialValues)) {
      this.patchProps({ style: { input: { border: fieldBox.outline } } });
    }
    if (shouldPatchProp(fieldBox?.outline, 'style.list.border', elementProps, initialValues)) {
      this.patchProps({ style: { list: { border: fieldBox.outline } } });
    }
    if (shouldPatchProp(fieldBox?.radius, 'style.input.radius', elementProps, initialValues)) {
      this.patchProps({ style: { input: { radius: fieldBox.radius } } });
    }
    if (shouldPatchProp(fieldBox?.radius, 'style.list.radius', elementProps, initialValues)) {
      this.patchProps({ style: { list: { radius: fieldBox.radius } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'dropdown'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
